import React, { useCallback } from 'react';
import X from '@uber/base-icons/x';
import { type Theme, useStyletron } from 'baseui';
import { Slider } from 'baseui/slider';
import { PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { REFERENCE_IMAGE } from '../../../constants/template-builder';
type ImageOpacitySliderPropsT = {
  opacity: number;
  onOpacityChange: (opacity: number) => void;
  onRemoveImage: () => void;
};
const getContainerStyles = (theme: Theme) => ({
  position: ('absolute' as const),
  bottom: theme.sizing.scale600,
  left: '50%',
  transform: 'translateX(-50%)',
  display: ('flex' as const),
  alignItems: ('center' as const),
  gap: theme.sizing.scale400,
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: theme.borders.radius300,
  boxShadow: theme.lighting.shadow400,
  paddingTop: theme.sizing.scale300,
  paddingBottom: theme.sizing.scale300,
  paddingLeft: theme.sizing.scale500,
  paddingRight: theme.sizing.scale400,
  pointerEvents: ('auto' as const)
});
const getPercentLabelStyles = (theme: Theme) => ({
  ...theme.typography.LabelSmall,
  color: theme.colors.contentPrimary,
  minWidth: '36px',
  textAlign: ('center' as const),
  whiteSpace: ('nowrap' as const)
});
const getSliderContainerStyles = () => ({
  width: REFERENCE_IMAGE.SLIDER_WIDTH
});
const getRemoveButtonStyles = (theme: Theme) => ({
  display: ('flex' as const),
  alignItems: ('center' as const),
  justifyContent: ('center' as const),
  width: theme.sizing.scale800,
  height: theme.sizing.scale800,
  background: 'none',
  border: 'none',
  cursor: ('pointer' as const),
  padding: '0',
  borderRadius: theme.borders.radius200,
  color: theme.colors.contentTertiary,
  ':hover': {
    color: theme.colors.contentPrimary,
    backgroundColor: theme.colors.backgroundTertiary
  }
});
const SLIDER_OVERRIDES = {
  InnerThumb: {
    style: () => ({
      display: 'none'
    })
  },
  ThumbValue: {
    style: () => ({
      display: 'none'
    })
  },
  TickBar: {
    style: () => ({
      display: 'none'
    })
  },
  Root: {
    style: () => ({
      marginTop: '0',
      marginBottom: '0'
    })
  },
  Track: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      paddingTop: $theme.sizing.scale200,
      paddingBottom: $theme.sizing.scale200
    })
  }
};
export function ImageOpacitySlider({
  opacity,
  onOpacityChange,
  onRemoveImage
}: ImageOpacitySliderPropsT) {
  const [css, theme] = useStyletron();
  const handleChange = useCallback(({
    value
  }: {
    value: number[];
  }) => {
    onOpacityChange(value[0] ?? REFERENCE_IMAGE.DEFAULT_OPACITY);
  }, [onOpacityChange]);
  const percentLabel = `${Math.round(opacity * 100)}%`;
  return <div className={css(getContainerStyles(theme))} data-testid="image-opacity-slider">
      <span className={css(getPercentLabelStyles(theme))}>{percentLabel}</span>
      <div className={css(getSliderContainerStyles())}>
        <Slider value={[opacity]} onChange={handleChange} min={REFERENCE_IMAGE.MIN_OPACITY} max={REFERENCE_IMAGE.MAX_OPACITY} step={REFERENCE_IMAGE.OPACITY_STEP} overrides={SLIDER_OVERRIDES} />
      </div>
      <StatefulTooltip content={REFERENCE_IMAGE.REMOVE_TOOLTIP} placement={PLACEMENT.top} showArrow>
        <button className={css(getRemoveButtonStyles(theme))} onClick={onRemoveImage} type="button" aria-label={REFERENCE_IMAGE.REMOVE_TOOLTIP} data-testid="remove-reference-image">
          <X size={14} />
        </button>
      </StatefulTooltip>
    </div>;
}